/**
 * Decode a numeric, seconds-based claim from a JWT payload as an epoch timestamp in
 * milliseconds, without verifying the signature (the server already verifies it).
 * Returns null when the token is malformed or the claim is absent/non-numeric. 🏴‍☠️
 */
const decodeJwtClaimMs = (token: string, claim: 'iat' | 'exp'): number | null => {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
    const claims = JSON.parse(atob(padded)) as Record<string, unknown>
    const value = claims[claim]
    return typeof value === 'number' ? value * 1000 : null
  } catch {
    return null
  }
}

/** When the access token was issued (epoch ms) — i.e. when it was last refreshed. */
export const decodeJwtIssuedAtMs = (token: string): number | null =>
  decodeJwtClaimMs(token, 'iat')

/**
 * True when the access token was issued at least `minAgeMs` ago. Used to throttle
 * keep-alive refreshes: we renew at most once per that window of activity/focus, which
 * also pre-empts the "Invalid or expired access token" burst when a token is already
 * stale. Returns false when there's no readable `iat`, so we fall back to the reactive
 * 401 refresh path instead of refreshing blindly.
 */
export const isAccessTokenOlderThan = (
  token: string,
  minAgeMs: number,
  now: number = Date.now()
): boolean => {
  const issuedAt = decodeJwtIssuedAtMs(token)
  if (issuedAt === null) return false
  return now - issuedAt >= minAgeMs
}
